import * as Sentry from "@sentry/react";

type ScrubbedRecord = Record<string, unknown>;

const PII_KEYS = [
  "email",
  "email_address",
  "phone",
  "phone_number",
  "first_name",
  "last_name",
  "full_name",
  "password",
  "access_token",
  "refresh_token",
  "apikey",
];

const scrubPii = (input: unknown): unknown => {
  if (!input || typeof input !== "object") return input;
  if (Array.isArray(input)) return input.map(scrubPii);
  const out: ScrubbedRecord = { ...(input as ScrubbedRecord) };
  for (const key of Object.keys(out)) {
    if (PII_KEYS.includes(key.toLowerCase())) {
      out[key] = "[scrubbed]";
    } else {
      out[key] = scrubPii(out[key]);
    }
  }
  return out;
};

export const initSentry = () => {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) return;

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    release: import.meta.env.VITE_SENTRY_RELEASE,
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true }),
    ],
    beforeSend(event) {
      if (event.request?.data) {
        event.request.data = scrubPii(event.request.data) as typeof event.request.data;
      }
      if (event.extra) {
        event.extra = scrubPii(event.extra) as typeof event.extra;
      }
      if (event.user) {
        delete event.user.email;
        delete event.user.username;
        delete event.user.ip_address;
      }
      return event;
    },
    beforeBreadcrumb(breadcrumb) {
      if (breadcrumb.data) {
        breadcrumb.data = scrubPii(breadcrumb.data) as typeof breadcrumb.data;
      }
      return breadcrumb;
    },
  });
};

export const captureAdminMutationError = (
  err: unknown,
  mutation: string,
  context?: Record<string, unknown>,
) => {
  Sentry.captureException(err, {
    tags: { area: "admin_mutation", mutation },
    extra: context ? (scrubPii(context) as Record<string, unknown>) : undefined,
  });
};

export const setSentryUser = (userId: string | null) => {
  Sentry.setUser(userId ? { id: userId } : null);
};

export { Sentry };
